/* =====================================================================
   STARTUS CLOUD — PC Builder engine (sc-pc-builder.js)
   Paired with sections/sc-pc-builder.liquid.

   The section renders every candidate part — price, stock, image and the
   spec.* metafields — into a JSON island. This file turns that into a
   configurator: it filters each step against what is already chosen, keeps a
   live wattage estimate and compatibility verdict, persists the build to
   localStorage and to a shareable URL, and adds the finished machine to the
   cart in a single request.

   COMPATIBILITY POLICY
     hard  — physically impossible. The part is filtered out and cannot be
             chosen. Only ever applied when BOTH sides of the comparison are
             actually known; a missing metafield never blocks a sale.
     soft  — judgement calls (PSU headroom, cooling, no display output). These
             surface in the checklist and never stop the shopper.
   ===================================================================== */
(function () {
  'use strict';

  var SC = (window.SC = window.SC || {});

  /* ────────────────────────────────────────────────────────── helpers ── */

  var LS_KEY = 'sc:build:v1';
  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };
  var uniq = function (a) { return a.filter(function (v, i) { return a.indexOf(v) === i; }); };
  var num = function (v) {
    if (v === null || v === undefined || v === '') return null;
    var n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? null : n;
  };
  var bool = function (v) { return v === true || v === 'true' || v === 1 || v === '1'; };

  /* Shopify's money format, implemented properly so whatever the merchant
     sets in Admin (Rs. / ₹ / decimals / separators) is what the shopper sees. */
  function formatMoney(cents, format) {
    var fmt = format || (window.Shopify && window.Shopify.moneyFormat) || '₹{{amount}}';
    var value = (cents || 0) / 100;

    function thousands(n, sep, dec, places) {
      if (isNaN(n)) n = 0;
      var fixed = Math.abs(n).toFixed(places === 0 ? 0 : (places || 2));
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + sep);
      return (n < 0 ? '-' : '') + whole + (parts[1] ? dec + parts[1] : '');
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return thousands(value, ',', '.', 2);
        case 'amount_no_decimals': return thousands(value, ',', '.', 0);
        case 'amount_with_comma_separator': return thousands(value, '.', ',', 2);
        case 'amount_no_decimals_with_comma_separator': return thousands(value, '.', ',', 0);
        case 'amount_with_space_separator': return thousands(value, ' ', ',', 2);
        case 'amount_no_decimals_with_space_separator': return thousands(value, ' ', ',', 0);
        case 'amount_with_period_and_space_separator': return thousands(value, ' ', '.', 2);
        default: return thousands(value, ',', '.', 2);
      }
    });
  }

  /* Money for the running total: whole rupees read better than ".00" on every
     line, but never round away real paise if the shop actually uses them. */
  function money(cents, fmt) {
    var s = formatMoney(cents, fmt);
    return (cents % 100 === 0) ? s.replace(/[.,]00\b/, '') : s;
  }

  function icon(name, cls) {
    return '<svg class="ic ' + (cls || '') + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<use href="#sc-i-' + esc(name) + '"/></svg>';
  }

  /* ─────────────────────────────────────────── spec derivation layer ──
     Reads a value from the metafield when it exists and falls back to what
     the product title reliably states. Keeps the builder correct against
     today's catalogue while the newer metafields are still being filled in. */

  function normForm(s) {
    if (!s) return null;
    var t = String(s).toLowerCase();
    if (/e-?atx/.test(t)) return 'E-ATX';
    if (/(micro|m)[\s-]?atx|matx/.test(t)) return 'Micro-ATX';
    if (/mini[\s-]?itx|\bitx\b/.test(t)) return 'Mini-ITX';
    if (/\batx\b/.test(t)) return 'ATX';
    return null;
  }

  /* Which board sizes a cabinet takes. Explicit metafield wins; otherwise the
     tower class in `form_factor` is read, which is how the catalogue records
     it today ("Mid Tower ATX", "Micro-ATX"). Unknown returns null = no block. */
  function caseBoardSupport(p) {
    var listed = p.s && p.s.mbsupport;
    if (Array.isArray(listed) && listed.length) {
      return uniq(listed.map(normForm).filter(Boolean));
    }
    var raw = (p.s && p.s.form) || p.t || '';
    var t = String(raw).toLowerCase();
    if (/full\s*tower/.test(t)) return ['E-ATX', 'ATX', 'Micro-ATX', 'Mini-ITX'];
    if (/mid\s*tower/.test(t)) return ['ATX', 'Micro-ATX', 'Mini-ITX'];
    var f = normForm(raw);
    if (f === 'E-ATX') return ['E-ATX', 'ATX', 'Micro-ATX', 'Mini-ITX'];
    if (f === 'ATX') return ['ATX', 'Micro-ATX', 'Mini-ITX'];
    if (f === 'Micro-ATX') return ['Micro-ATX', 'Mini-ITX'];
    if (f === 'Mini-ITX') return ['Mini-ITX'];
    return null;
  }

  function memGeneration(p) {
    var v = p.s && p.s.mem;
    if (v) { var n = normMem(v); if (n) return n; }
    return normMem(p.t) || normMem(p.n);
  }
  function normMem(s) {
    if (!s) return null;
    var m = String(s).toUpperCase().match(/DDR\s?-?([345])/);
    return m ? 'DDR' + m[1] : null;
  }

  /* Sockets a cooler mounts on. Coolers ship with universal bracket kits, so
     an unknown value must not block — it returns null and the rule is skipped. */
  function coolerSockets(p) {
    var listed = p.s && p.s.sockets;
    if (Array.isArray(listed) && listed.length) return listed.map(function (x) { return String(x).toUpperCase().replace(/\s+/g, ''); });
    if (typeof listed === 'string' && listed) return listed.split(/[,/]/).map(function (x) { return x.trim().toUpperCase().replace(/\s+/g, ''); }).filter(Boolean);
    var found = String(p.t || '').toUpperCase().match(/(LGA\s?\d{4}|AM[45])/g);
    if (found && found.length) return uniq(found.map(function (x) { return x.replace(/\s+/g, ''); }));
    return null;
  }
  var normSocket = function (s) { return s ? String(s).toUpperCase().replace(/\s+/g, '') : null; };

  function isLiquid(p) {
    var k = (p.s && p.s.kind) || '';
    if (/liquid|aio/i.test(k)) return true;
    if (p.s && num(p.s.rad)) return true;
    return /liquid|aio|\bh\d{2,3}i\b|freezer/i.test(p.t || '');
  }

  /* ─────────────────────────────────────────────────────────── engine ── */

  function Builder(root) {
    this.root = root;
    this.cfg = null;
    this.steps = [];
    this.byId = {};
    this.sel = {};            // stepKey -> [{ id, qty }]
    this.active = 0;
    this.query = '';
    this.sort = 'recommended';
    this.compatOnly = true;
    this.facet = null;        // { key, value }
    this.facetsOpen = false;
    this.busy = false;
    this.init();
  }

  Builder.prototype.init = function () {
    var raw = $('[data-bld-catalog]', this.root);
    if (!raw) return;
    try { this.cfg = JSON.parse(raw.textContent); }
    catch (e) { console.error('[sc-pc-builder] catalogue JSON is malformed', e); return; }

    var self = this;
    this.steps = (this.cfg.steps || []).filter(function (s) {
      s.parts = (s.parts || []).filter(function (p) { return p && p.id && p.vid; });
      // A step with nothing behind it is hidden unless the merchant asked to
      // keep it visible, in which case it renders an honest empty state.
      return s.parts.length > 0 || !s.hide_empty;
    });
    this.steps.forEach(function (s) {
      s.parts.forEach(function (p) { self.byId[p.id] = { part: p, step: s }; });
      self.sel[s.key] = [];
    });

    if (!this.steps.length) { this.renderNothing(); return; }

    this.cacheDom();
    this.bind();
    this.restore();
    this.renderPresets();
    this.renderAll();

    var lost = [];
    this.steps.forEach(function (s) {
      (s.truncated || []).filter(Boolean).forEach(function (c) { lost.push(s.label + ' → ' + c); });
    });
    if (lost.length && window.console) {
      console.warn('[sc-pc-builder] Liquid returns at most 50 products per collection, and these ' +
        'are over it while still being the only source for some of their products, so parts are ' +
        'being dropped:\n  ' + lost.join('\n  ') +
        '\nList additional narrower collections on the step so every product is reachable.');
    }
  };

  Builder.prototype.cacheDom = function () {
    var r = this.root;
    this.el = {
      steps: $('[data-bld-steps]', r),
      chips: $('[data-bld-chips]', r),
      parts: $('[data-bld-parts]', r),
      empty: $('[data-bld-empty]', r),
      emptyTitle: $('[data-bld-empty-title]', r),
      emptyBody: $('[data-bld-empty-body]', r),
      emptyActs: $('[data-bld-empty-actions]', r),
      label: $('[data-bld-panel-label]', r),
      note: $('[data-bld-panel-note]', r),
      count: $('[data-bld-count]', r),
      search: $('[data-bld-search]', r),
      searchClr: $('[data-bld-search-clear]', r),
      sort: $('[data-bld-sort]', r),
      compat: $('[data-bld-compat-only]', r),
      hidden: $('[data-bld-hidden-count]', r),
      facets: $('[data-bld-facets]', r),
      skip: $('[data-bld-skip]', r),
      lines: $('[data-bld-lines]', r),
      issues: $('[data-bld-issues]', r),
      healthDot: $('[data-bld-health-dot]', r),
      healthTtl: $('[data-bld-health-title]', r),
      powerVal: $('[data-bld-power-value]', r),
      powerBar: $('[data-bld-power-bar]', r),
      powerNote: $('[data-bld-power-note]', r),
      total: $('[data-bld-total]', r),
      mrp: $('[data-bld-mrp]', r),
      mrpRow: $('[data-bld-mrp-row]', r),
      save: $('[data-bld-save]', r),
      saveRow: $('[data-bld-save-row]', r),
      add: $('[data-bld-add]', r),
      addLabel: $('[data-bld-add-label]', r),
      status: $('[data-bld-status]', r),
      ring: $('[data-bld-ring]', r),
      progCount: $('[data-bld-progress-count]', r),
      progLabel: $('[data-bld-progress-label]', r),
      summary: $('[data-bld-summary]', r),
      dock: $('[data-bld-dock]', r),
      dockCount: $('[data-bld-dock-count]', r),
      dockTotal: $('[data-bld-dock-total]', r),
      dockHealth: $('[data-bld-dock-health]', r),
      scrim: $('[data-bld-scrim]', r),
      scroll: $('[data-bld-scroll]', r),
      presets: $('[data-bld-presets]', r),
      presetRail: $('[data-bld-preset-rail]', r),
      presetPrev: $('[data-bld-preset-prev]', r),
      presetNext: $('[data-bld-preset-next]', r)
    };
  };

  /* ───────────────────────────────────────────── selection accessors ── */

  Builder.prototype.step = function (i) { return this.steps[i == null ? this.active : i]; };
  Builder.prototype.stepByRole = function (role) {
    for (var i = 0; i < this.steps.length; i++) if (this.steps[i].role === role) return this.steps[i];
    return null;
  };
  /* The single chosen part for a role — the value every rule reasons about. */
  Builder.prototype.pick = function (role) {
    var s = this.stepByRole(role);
    if (!s) return null;
    var list = this.sel[s.key] || [];
    if (!list.length) return null;
    var rec = this.byId[list[0].id];
    return rec ? rec.part : null;
  };
  Builder.prototype.picks = function (role) {
    var s = this.stepByRole(role), self = this;
    if (!s) return [];
    return (this.sel[s.key] || []).map(function (x) {
      var rec = self.byId[x.id];
      return rec ? { part: rec.part, qty: x.qty } : null;
    }).filter(Boolean);
  };
  Builder.prototype.isSelected = function (stepKey, id) {
    return (this.sel[stepKey] || []).some(function (x) { return x.id === id; });
  };
  Builder.prototype.qtyOf = function (stepKey, id) {
    var hit = (this.sel[stepKey] || []).filter(function (x) { return x.id === id; })[0];
    return hit ? hit.qty : 0;
  };
  Builder.prototype.selectedCount = function () {
    var n = 0, self = this;
    Object.keys(this.sel).forEach(function (k) { n += self.sel[k].length; });
    return n;
  };

  /* ────────────────────────────────────────── compatibility — hard ──
     Returns null when the part may be chosen, otherwise a short shopper-facing
     reason. Every branch demands BOTH values be known before it blocks. */

  Builder.prototype.blockReason = function (part, step) {
    var role = step.role;
    var cpu = this.pick('cpu'),
      mb = this.pick('motherboard'),
      cab = this.pick('cabinet'),
      gpu = this.pick('gpu'),
      cool = this.pick('cooler');

    if (role === 'cpu' && mb) {
      var a = normSocket(part.s.socket), b = normSocket(mb.s.socket);
      if (a && b && a !== b) return 'Needs an ' + b + ' processor for the ' + mb.n;
    }

    if (role === 'motherboard') {
      if (cpu) {
        var ms = normSocket(part.s.socket), cs = normSocket(cpu.s.socket);
        if (ms && cs && ms !== cs) return cpu.n + ' needs an ' + cs + ' socket';
      }
      if (cab) {
        var sup = caseBoardSupport(cab), bf = normForm(part.s.form);
        if (sup && bf && sup.indexOf(bf) === -1) return bf + ' will not fit the ' + cab.n;
      }
      var ramNow = this.picks('memory')[0];
      if (ramNow) {
        var bm = memGeneration(part), rm = memGeneration(ramNow.part);
        if (bm && rm && bm !== rm) return 'Your ' + rm + ' memory needs a ' + rm + ' board';
      }
    }

    if (role === 'memory' && mb) {
      var boardMem = memGeneration(mb), partMem = memGeneration(part);
      if (boardMem && partMem && boardMem !== partMem) return mb.n + ' takes ' + boardMem + ' only';
    }

    if (role === 'gpu' && cab) {
      var maxLen = num(cab.s.maxgpu), len = num(part.s.len);
      if (maxLen && len && len > maxLen) return 'Too long for the ' + cab.n + ' (' + maxLen + ' mm limit)';
    }

    if (role === 'cooler') {
      if (cpu) {
        var socks = coolerSockets(part), cSock = normSocket(cpu.s.socket);
        if (socks && cSock && socks.indexOf(cSock) === -1) return 'Does not mount on ' + cSock;
      }
      if (cab) {
        if (isLiquid(part)) {
          var rad = num(part.s.rad), rads = cab.s.rads;
          if (rad && Array.isArray(rads) && rads.length &&
            rads.map(num).filter(Boolean).indexOf(rad) === -1) {
            return 'The ' + cab.n + ' has no ' + rad + ' mm radiator mount';
          }
        } else {
          var h = num(part.s.height), maxH = num(cab.s.maxcooler);
          if (h && maxH && h > maxH) return 'Too tall for the ' + cab.n + ' (' + maxH + ' mm limit)';
        }
      }
    }

    if (role === 'cabinet') {
      if (mb) {
        var support = caseBoardSupport(part), form = normForm(mb.s.form);
        if (support && form && support.indexOf(form) === -1) return 'Will not take an ' + form + ' board';
      }
      if (gpu) {
        var cmax = num(part.s.maxgpu), glen = num(gpu.s.len);
        if (cmax && glen && glen > cmax) return gpu.n + ' is too long for this case';
      }
      if (cool && !isLiquid(cool)) {
        var ch = num(cool.s.height), cm = num(part.s.maxcooler);
        if (ch && cm && ch > cm) return 'Your cooler is too tall for this case';
      }
      if (cool && isLiquid(cool)) {
        var crad = num(cool.s.rad), crads = part.s.rads;
        if (crad && Array.isArray(crads) && crads.length &&
          crads.map(num).filter(Boolean).indexOf(crad) === -1) {
          return 'No ' + crad + ' mm radiator mount for your cooler';
        }
      }
    }

    return null;
  };

  /* ─────────────────────────────────────────────── power estimate ──
     CPU figures are TDP, not peak. Unlocked parts boost far past it, so the
     estimate scales TDP toward a realistic sustained draw rather than quoting
     a number that would under-size the PSU. */

  Builder.prototype.power = function () {
    var cpu = this.pick('cpu'), gpu = this.pick('gpu');
    var load = 0, known = false;

    if (cpu) {
      var tdp = num(cpu.s.tdp);
      if (tdp) { load += Math.round(tdp * (bool(cpu.s.unlocked) ? 1.7 : 1.25)); known = true; }
    }
    if (gpu) {
      var gt = num(gpu.s.tdp);
      if (gt) { load += Math.round(gt * 1.1); known = true; }
    }

    var drives = this.picks('storage').reduce(function (n, x) { return n + x.qty; }, 0);
    var fans = this.picks('fans').reduce(function (n, x) { return n + x.qty; }, 0);
    var sticks = this.picks('memory').reduce(function (n, x) { return n + x.qty; }, 0);

    // Board, memory, drives, fans and the pump on an AIO.
    var base = 55 + (drives * 8) + (fans * 6) + (sticks * 6);
    var cooler = this.pick('cooler');
    if (cooler && isLiquid(cooler)) base += 12;
    if (known) load += base;

    // Vendors publish a minimum PSU per card; never recommend under it.
    var vendorMin = gpu ? num(gpu.s.psumin) : null;
    var headroom = Math.ceil((load / 0.65) / 50) * 50;
    var recommend = Math.max(headroom, vendorMin || 0);

    return { load: known ? load : 0, known: known, recommend: known ? recommend : (vendorMin || 0) };
  };

  /* One verdict on the power supply, read by both the meter and the checklist.
     Anything that makes the build unsafe to ship — under the estimated draw, or
     under the card vendor's stated minimum — is `bad`; thin headroom is `warn`. */
  Builder.prototype.psuVerdict = function () {
    var psu = this.pick('psu'), gpu = this.pick('gpu'), pw = this.power();
    if (!psu || !pw.known) return { level: 'idle', pct: 0, load: pw.load, recommend: pw.recommend };
    var w = num(psu.s.watts);
    if (!w) return { level: 'idle', pct: 0, load: pw.load, recommend: pw.recommend };
    var vmin = gpu ? num(gpu.s.psumin) : null;
    var pct = Math.min(100, Math.round((pw.load / w) * 100));
    var head = Math.round(((w / pw.load) - 1) * 100);
    var base = { pct: pct, w: w, vmin: vmin, head: head, load: pw.load, recommend: pw.recommend, psu: psu, gpu: gpu };
    if (w < pw.load || (vmin && w < vmin)) base.level = 'bad';
    else if (w < pw.load * 1.2) base.level = 'warn';
    else base.level = 'ok';
    return base;
  };

  /* ───────────────────────────────────────── compatibility — soft ──
     The checklist. `level` is ok | info | warn | bad; nothing here blocks. */

  Builder.prototype.issues = function () {
    var out = [];
    var cpu = this.pick('cpu'), gpu = this.pick('gpu'), psu = this.pick('psu'),
      mb = this.pick('motherboard'), cool = this.pick('cooler'), cab = this.pick('cabinet');
    var mem = this.picks('memory'), sto = this.picks('storage');
    var pw = this.power();

    // Required steps still open.
    var missing = this.steps.filter(function (s) {
      return s.required && s.parts.length && !(this.sel[s.key] || []).length;
    }, this);
    missing.forEach(function (s) {
      out.push({ level: 'info', icon: 'plus', text: 'Still to choose: ' + s.label, step: s.key });
    });

    if (cpu && mb) {
      out.push({ level: 'ok', icon: 'check', text: normSocket(cpu.s.socket) ? (normSocket(cpu.s.socket) + ' socket matched') : 'Processor and board matched' });
    }

    if (mem.length && mb) {
      var bm = memGeneration(mb), rm = memGeneration(mem[0].part);
      if (bm && rm && bm === rm) out.push({ level: 'ok', icon: 'check', text: bm + ' memory matched to the board' });
      var slots = num(mb.s.slots);
      var sticksTotal = mem.reduce(function (n, x) {
        var per = num(x.part.s.sticks) || 1;
        return n + (per * x.qty);
      }, 0);
      if (slots && sticksTotal > slots) {
        out.push({ level: 'bad', icon: 'warning', text: 'That is ' + sticksTotal + ' memory sticks but the ' + mb.n + ' has ' + slots + ' slots.' });
      }
    }

    // Until the required steps are done the shopper is mid-flow, so "you have no
    // graphics card yet" is guidance, not a defect. It hardens to a blocker once
    // the build is otherwise complete.
    var stillBuilding = missing.length > 0;
    if (cpu && !gpu) {
      if (bool(cpu.s.igpu)) {
        out.push({ level: 'info', icon: 'info', text: cpu.n + ' has integrated graphics, so a card is optional.' });
      } else {
        out.push({
          level: stillBuilding ? 'warn' : 'bad', icon: 'warning',
          text: cpu.n + ' has no integrated graphics — without a card this PC has no display output.',
          step: (this.stepByRole('gpu') || {}).key
        });
      }
    }

    if (cpu && !cool) {
      if (bool(cpu.s.boxcooler)) {
        out.push({ level: 'ok', icon: 'check', text: 'Boxed cooler included with the ' + cpu.n + '.' });
      } else {
        var lvl = (!stillBuilding && (num(cpu.s.tdp) || 0) >= 100) ? 'bad' : 'warn';
        out.push({ level: lvl, icon: 'warning', text: cpu.n + ' ships without a cooler. Add CPU cooling.', step: (this.stepByRole('cooler') || {}).key });
      }
    }

    if (cpu && cool) {
      var cap = num(cool.s.cap), ctdp = num(cpu.s.tdp);
      if (cap && ctdp) {
        var need = Math.round(ctdp * (bool(cpu.s.unlocked) ? 1.7 : 1.25));
        if (cap < need) out.push({ level: 'warn', icon: 'warning', text: cool.n + ' is rated to ' + cap + ' W; the ' + cpu.n + ' can pull about ' + need + ' W under sustained load.' });
        else out.push({ level: 'ok', icon: 'check', text: 'Cooling rated above the processor\'s sustained draw.' });
      }
    }

    var pv = this.psuVerdict();
    if (pv.level !== 'idle') {
      var psuStep = (this.stepByRole('psu') || {}).key;
      if (pv.w < pv.load) {
        out.push({
          level: 'bad', icon: 'bolt', step: psuStep,
          text: pv.psu.n + ' is ' + pv.w + ' W but the build draws about ' + pv.load + ' W. Move up to at least ' + pv.recommend + ' W.'
        });
      } else if (pv.vmin && pv.w < pv.vmin) {
        out.push({
          level: 'bad', icon: 'bolt', step: psuStep,
          text: pv.gpu.n + ' is specified for a ' + pv.vmin + ' W supply minimum — ' + pv.psu.n + ' is ' + pv.w + ' W.'
        });
      } else if (pv.level === 'warn') {
        out.push({
          level: 'warn', icon: 'bolt', step: psuStep,
          text: 'Only ' + pv.head + '% headroom on the ' + pv.w + ' W unit. ' + pv.recommend + ' W or more is the comfortable choice.'
        });
      } else {
        out.push({ level: 'ok', icon: 'check', text: pv.w + ' W with ' + pv.head + '% headroom.' });
      }
    }

    if (cab && mb) {
      var sup = caseBoardSupport(cab), bf = normForm(mb.s.form);
      if (sup && bf && sup.indexOf(bf) > -1) out.push({ level: 'ok', icon: 'check', text: bf + ' board fits the ' + cab.n + '.' });
    }

    if (sto.length) {
      var hasFast = sto.some(function (x) { return /nvme|pcie/i.test(x.part.s.iface || x.part.t || ''); });
      if (!hasFast) out.push({ level: 'info', icon: 'info', text: 'No NVMe drive selected — Windows will feel noticeably slower on SATA.' });
      var m2 = mb ? num(mb.s.m2) : null;
      var nvme = sto.filter(function (x) { return /nvme|m\.?2/i.test((x.part.s.iface || '') + ' ' + (x.part.s.form || '')); })
        .reduce(function (n, x) { return n + x.qty; }, 0);
      if (m2 && nvme > m2) out.push({ level: 'bad', icon: 'warning', text: 'That is ' + nvme + ' M.2 drives but the ' + mb.n + ' has ' + m2 + ' slots.' });
    }

    return out;
  };

  /* Overall verdict shown at the top of the summary. */
  Builder.prototype.verdict = function () {
    var issues = this.issues();
    var openRequired = this.steps.filter(function (s) {
      return s.required && s.parts.length && !(this.sel[s.key] || []).length;
    }, this);

    if (!this.selectedCount()) return { level: 'idle', title: 'Pick a processor to begin', issues: issues };
    if (issues.some(function (i) { return i.level === 'bad'; })) return { level: 'bad', title: 'Needs attention before this ships', issues: issues };
    if (openRequired.length) return { level: 'info', title: openRequired.length + ' part' + (openRequired.length > 1 ? 's' : '') + ' still to choose', issues: issues };
    if (issues.some(function (i) { return i.level === 'warn'; })) return { level: 'warn', title: 'Buildable, with one thing to consider', issues: issues };
    return { level: 'ok', title: 'Compatible and ready to build', issues: issues };
  };

  Builder.prototype.totals = function () {
    var price = 0, mrp = 0, self = this;
    Object.keys(this.sel).forEach(function (k) {
      self.sel[k].forEach(function (x) {
        var rec = self.byId[x.id];
        if (!rec) return;
        price += rec.part.p * x.qty;
        mrp += (rec.part.cp > rec.part.p ? rec.part.cp : rec.part.p) * x.qty;
      });
    });
    return { price: price, mrp: mrp, save: Math.max(0, mrp - price) };
  };

  /* ──────────────────────────────────────────────────────── rendering ── */

  /* Two or three specs that actually help a shopper choose, per role. */
  function specChips(p, role) {
    var s = p.s || {}, out = [];
    var push = function (v) { if (v) out.push(v); };
    switch (role) {
      case 'cpu':
        push(s.socket);
        push(s.cores ? s.cores + 'C / ' + (s.threads || s.cores) + 'T' : null);
        push(s.boost ? 'up to ' + s.boost + ' GHz' : null);
        push(s.tdp ? s.tdp + ' W' : null);
        break;
      case 'motherboard':
        push(s.chipset); push(s.socket); push(s.form); push(s.mem);
        if (bool(s.wifi)) push('Wi-Fi');
        break;
      case 'memory':
        push(s.gb ? s.gb + ' GB' : null);
        push(s.sticks ? s.sticks + ' x ' + (s.gb ? (s.gb / s.sticks) + ' GB' : 'module') : null);
        push(s.mem || memGeneration(p));
        push(s.speed ? s.speed + ' MHz' : null);
        break;
      case 'gpu':
        push(s.vram ? s.vram + ' GB' : null); push(s.res); push(s.tdp ? s.tdp + ' W' : null);
        push(s.psumin ? s.psumin + ' W PSU' : null);
        break;
      case 'storage':
        push(s.gb ? (s.gb >= 1000 ? (s.gb / 1000) + ' TB' : s.gb + ' GB') : null);
        push(s.iface); push(s.read ? s.read + ' MB/s' : null);
        break;
      case 'psu':
        push(s.watts ? s.watts + ' W' : null); push(s.eff); push(s.mod);
        break;
      case 'cabinet':
        push(s.form);
        push(s.maxgpu ? 'GPU to ' + s.maxgpu + ' mm' : null);
        if (bool(s.rgb)) push('ARGB');
        break;
      case 'cooler':
        push(s.kind); push(s.cap ? s.cap + ' W rated' : null);
        push(s.rad ? s.rad + ' mm radiator' : (s.height ? s.height + ' mm tall' : null));
        break;
      default:
        push(s.size ? s.size + '"' : null); push(s.res); push(s.hz ? s.hz + ' Hz' : null);
        push(s.panel); push(s.switch); push(s.dpi ? s.dpi + ' DPI' : null); push(s.conn);
    }
    return out.slice(0, 4);
  }

  Builder.prototype.money = function (c) { return money(c, this.cfg.money_format); };

  Builder.prototype.partCard = function (p, step, blocked) {
    var selected = this.isSelected(step.key, p.id);
    var off = (p.cp > p.p) ? Math.round((1 - p.p / p.cp) * 100) : 0;
    var chips = specChips(p, step.role);
    var qty = this.qtyOf(step.key, p.id);
    var soldOut = !p.av;

    var cls = ['bld-part'];
    if (selected) cls.push('is-picked');
    if (blocked) cls.push('is-blocked');
    if (soldOut) cls.push('is-out');

    var h = '';
    h += '<article class="' + cls.join(' ') + '" role="listitem" data-bld-part="' + p.id + '" data-step="' + esc(step.key) + '">';

    h += '<div class="bld-part-media">';
    h += p.img
      ? '<img src="' + esc(p.img) + '" alt="" width="120" height="120" loading="lazy" decoding="async">'
      : '<span class="bld-part-noimg" aria-hidden="true">' + icon(step.icon || 'cpu', 'ic-24') + '</span>';
    if (selected) h += '<span class="bld-part-tick" aria-hidden="true">' + icon('check', 'ic-14') + '</span>';
    h += '</div>';

    h += '<div class="bld-part-body">';
    if (p.br) h += '<span class="bld-part-brand">' + esc(p.br) + '</span>';
    h += '<h3 class="bld-part-title">' + esc(p.n) + '</h3>';
    if (chips.length) {
      h += '<ul class="bld-part-specs" role="list">';
      chips.forEach(function (c) { h += '<li>' + esc(c) + '</li>'; });
      h += '</ul>';
    }
    if (blocked) {
      h += '<p class="bld-part-block">' + icon('lock', 'ic-14') + '<span>' + esc(blocked) + '</span></p>';
    } else if (soldOut) {
      h += '<p class="bld-part-block">' + icon('info', 'ic-14') + '<span>Out of stock</span></p>';
    }
    h += '<a class="bld-part-link" href="' + esc(p.u) + '" target="_blank" rel="noopener">Full specification' +
      icon('swap', 'ic-12') + '</a>';
    h += '</div>';

    h += '<div class="bld-part-buy">';
    h += '<div class="bld-part-price">';
    h += '<b>' + esc(this.money(p.p)) + '</b>';
    if (off > 0) {
      h += '<s>' + esc(this.money(p.cp)) + '</s>';
      h += '<em>' + off + '% off</em>';
    }
    h += '</div>';

    if (blocked) {
      // Not a dead end: swapping platform is legitimate, it just means the parts
      // that depended on the old choice have to go. Say so, then let them do it.
      h += '<button type="button" class="btn btn-outline btn-sm bld-part-switch" data-bld-switch>' +
        icon('swap', 'ic-14') + ' Switch to this</button>';
    } else if (soldOut) {
      h += '<button type="button" class="btn btn-outline btn-sm" disabled>Out of stock</button>';
    } else if (selected && step.multi && step.max > 1) {
      h += '<div class="bld-qty">';
      h += '<button type="button" data-bld-qty="-1" aria-label="Reduce quantity">&minus;</button>';
      h += '<span aria-live="polite">' + qty + '</span>';
      h += '<button type="button" data-bld-qty="1" aria-label="Increase quantity"' + (qty >= step.max ? ' disabled' : '') + '>+</button>';
      h += '</div>';
      h += '<button type="button" class="bld-part-remove" data-bld-remove>Remove</button>';
    } else if (selected) {
      h += '<button type="button" class="btn btn-ink btn-sm" data-bld-choose>' + icon('check', 'ic-14') + ' Chosen</button>';
    } else {
      h += '<button type="button" class="btn btn-outline btn-sm bld-choose" data-bld-choose>' +
        (step.multi ? 'Add' : 'Choose') + '</button>';
    }
    h += '</div>';
    h += '</article>';
    return h;
  };

  /* Facet values worth offering for this step, built from the parts present. */
  function facetsFor(parts, role) {
    var key = role === 'cpu' ? 'series'
      : role === 'motherboard' ? 'chipset'
        : role === 'memory' ? 'mem'
          : role === 'gpu' ? 'res'
            : role === 'storage' ? 'iface'
              : role === 'psu' ? 'eff'
                : role === 'cabinet' ? 'form'
                  : role === 'cooler' ? 'kind'
                    : null;
    var byBrand = {}, byKey = {};
    parts.forEach(function (p) {
      if (p.br) byBrand[p.br] = (byBrand[p.br] || 0) + 1;
      var v = key && p.s ? p.s[key] : null;
      if (v) byKey[v] = (byKey[v] || 0) + 1;
    });
    var groups = [];
    var brands = Object.keys(byBrand).sort();
    if (brands.length > 1) groups.push({ id: 'brand', label: 'Brand', values: brands.map(function (b) { return { v: b, n: byBrand[b] }; }) });
    var vals = Object.keys(byKey).sort();
    if (key && vals.length > 1) groups.push({ id: key, label: '', values: vals.map(function (b) { return { v: b, n: byKey[b] }; }) });
    return groups;
  }

  Builder.prototype.visibleParts = function (step) {
    var self = this;
    var q = this.query.trim().toLowerCase();
    var rows = step.parts.map(function (p) {
      return { p: p, blocked: self.blockReason(p, step) };
    });

    if (q) {
      rows = rows.filter(function (r) {
        var hay = (r.p.t + ' ' + r.p.br + ' ' + r.p.sku + ' ' +
          Object.keys(r.p.s || {}).map(function (k) { return r.p.s[k]; }).join(' ')).toLowerCase();
        return q.split(/\s+/).every(function (w) { return hay.indexOf(w) > -1; });
      });
    }

    if (this.facet) {
      var f = this.facet;
      rows = rows.filter(function (r) {
        return f.id === 'brand' ? r.p.br === f.value : (r.p.s && r.p.s[f.id]) === f.value;
      });
    }

    var hiddenByCompat = rows.filter(function (r) { return !!r.blocked; }).length;
    if (this.compatOnly) rows = rows.filter(function (r) { return !r.blocked; });

    var sort = this.sort;
    rows.sort(function (a, b) {
      if (a.p.av !== b.p.av) return a.p.av ? -1 : 1;
      if (sort === 'price-asc') return a.p.p - b.p.p;
      if (sort === 'price-desc') return b.p.p - a.p.p;
      if (sort === 'name') return a.p.n.localeCompare(b.p.n);
      if (!!a.blocked !== !!b.blocked) return a.blocked ? 1 : -1;
      var ra = num(a.p.rt) || 0, rb = num(b.p.rt) || 0;
      if (rb !== ra) return rb - ra;
      return a.p.p - b.p.p;
    });

    return { rows: rows, hiddenByCompat: hiddenByCompat };
  };

  Builder.prototype.renderParts = function () {
    var step = this.step();
    if (!step) return;
    var res = this.visibleParts(step);
    var self = this;

    this.el.label.textContent = step.label;
    this.el.note.textContent = step.note || '';
    this.el.note.hidden = !step.note;

    this.el.hidden.textContent = res.hiddenByCompat ? '(' + res.hiddenByCompat + ' hidden)' : '';
    this.el.skip.hidden = step.required || !step.parts.length;

    var total = step.parts.length;
    this.el.count.textContent = total
      ? res.rows.length + ' of ' + total + ' ' + (total === 1 ? 'part' : 'parts') + ' shown'
      : '';

    // Facets
    var groups = facetsFor(step.parts, step.role);
    var CAP = 6;
    var fh = '';
    groups.forEach(function (g, gi) {
      if (gi) fh += '<span class="bld-facet-sep" aria-hidden="true"></span>';
      var vals = g.values.slice().sort(function (a, b) { return b.n - a.n; });
      var overflow = vals.length > CAP && !self.facetsOpen;
      (overflow ? vals.slice(0, CAP) : vals).forEach(function (val) {
        var on = self.facet && self.facet.id === g.id && self.facet.value === val.v;
        fh += '<button type="button" class="bld-facet' + (on ? ' is-on' : '') + '" ' +
          'data-bld-facet="' + esc(g.id) + '" data-value="' + esc(val.v) + '" aria-pressed="' + (on ? 'true' : 'false') + '">' +
          esc(val.v) + '<span>' + val.n + '</span></button>';
      });
      if (overflow) {
        fh += '<button type="button" class="bld-facet bld-facet-more" data-bld-facet-more>+' +
          (vals.length - CAP) + ' more</button>';
      }
    });
    if (self.facetsOpen && groups.some(function (g) { return g.values.length > CAP; })) {
      fh += '<button type="button" class="bld-facet bld-facet-more" data-bld-facet-more>Show fewer</button>';
    }
    this.el.facets.innerHTML = fh;
    this.el.facets.hidden = !fh;

    // Cards
    if (!res.rows.length) {
      this.el.parts.innerHTML = '';
      this.el.parts.hidden = true;
      this.el.empty.hidden = false;
      this.renderEmpty(step, res);
    } else {
      this.el.empty.hidden = true;
      this.el.parts.hidden = false;
      this.el.parts.innerHTML = res.rows.map(function (r) {
        return self.partCard(r.p, step, r.blocked);
      }).join('');
    }
  };

  Builder.prototype.renderEmpty = function (step, res) {
    var acts = '';
    if (!step.parts.length) {
      this.el.emptyTitle.textContent = step.label + ' is not stocked yet';
      this.el.emptyBody.textContent =
        'Nothing is listed in this step\'s collection at the moment. Everything else in the build still works — ' +
        'you can carry on and add this part later.';
      if (!step.required) acts = '<button type="button" class="btn btn-outline btn-sm" data-bld-skip-2>Continue without it</button>';
    } else if (this.query || this.facet) {
      this.el.emptyTitle.textContent = 'Nothing matches those filters';
      this.el.emptyBody.textContent = 'Clear the search or filter to see all ' + step.parts.length + ' parts in this step.';
      acts = '<button type="button" class="btn btn-outline btn-sm" data-bld-clear-filters>Clear filters</button>';
    } else if (res.hiddenByCompat) {
      this.el.emptyTitle.textContent = 'Nothing here fits the parts you have chosen';
      this.el.emptyBody.textContent = res.hiddenByCompat + ' ' + (res.hiddenByCompat === 1 ? 'part is' : 'parts are') +
        ' hidden because they will not work with your current selection. Change an earlier part, or show everything to see why.';
      acts = '<button type="button" class="btn btn-outline btn-sm" data-bld-show-all>Show incompatible parts</button>';
    } else {
      this.el.emptyTitle.textContent = 'Nothing to show';
      this.el.emptyBody.textContent = 'This step has no parts available right now.';
    }
    this.el.emptyActs.innerHTML = acts;
  };

  Builder.prototype.renderSteps = function () {
    var self = this, sh = '', ch = '';
    this.steps.forEach(function (s, i) {
      var chosen = (self.sel[s.key] || []);
      var rec = chosen.length ? self.byId[chosen[0].id] : null;
      var done = chosen.length > 0;
      var isActive = i === self.active;
      var sub = done
        ? (rec ? esc(rec.part.n) + (chosen.length > 1 ? ' +' + (chosen.length - 1) + ' more' : '') : '')
        : (s.required ? 'Required' : 'Optional');

      sh += '<li><button type="button" class="bld-step' + (isActive ? ' is-active' : '') + (done ? ' is-done' : '') + '" ' +
        'data-bld-goto="' + i + '"' + (isActive ? ' aria-current="step"' : '') + '>' +
        '<span class="bld-step-ic" aria-hidden="true">' + icon(done ? 'check' : (s.icon || 'cpu'), 'ic-16') + '</span>' +
        '<span class="bld-step-txt"><b>' + esc(s.label) + '</b><small>' + sub + '</small></span>' +
        (done ? '<span class="bld-step-price">' + esc(self.money(chosen.reduce(function (n, x) {
          var r = self.byId[x.id]; return n + (r ? r.part.p * x.qty : 0);
        }, 0))) + '</span>' : '') +
        '</button></li>';

      ch += '<button type="button" class="bld-chip' + (isActive ? ' is-active' : '') + (done ? ' is-done' : '') + '" ' +
        'data-bld-goto="' + i + '">' + icon(done ? 'check' : (s.icon || 'cpu'), 'ic-14') +
        '<span>' + esc(s.label) + '</span></button>';
    });
    this.el.steps.innerHTML = sh;
    this.el.chips.innerHTML = ch;

    var chip = this.el.chips.querySelector('.is-active');
    if (chip && chip.scrollIntoView) chip.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  };

  Builder.prototype.renderSummary = function () {
    var self = this, lines = '';

    this.steps.forEach(function (s, i) {
      var chosen = self.sel[s.key] || [];
      if (!chosen.length) {
        if (!s.required || !s.parts.length) return;
        lines += '<li class="bld-line bld-line-open">' +
          '<span class="bld-line-ic" aria-hidden="true">' + icon(s.icon || 'cpu', 'ic-16') + '</span>' +
          '<span class="bld-line-body"><small>' + esc(s.label) + '</small>' +
          '<button type="button" class="bld-line-pick" data-bld-goto="' + i + '">Choose ' + esc(s.label.toLowerCase()) + '</button></span>' +
          '</li>';
        return;
      }
      chosen.forEach(function (x) {
        var rec = self.byId[x.id];
        if (!rec) return;
        var p = rec.part;
        lines += '<li class="bld-line">' +
          '<span class="bld-line-ic" aria-hidden="true">' + icon(s.icon || 'cpu', 'ic-16') + '</span>' +
          '<span class="bld-line-body">' +
          '<small>' + esc(s.label) + (x.qty > 1 ? ' &times;' + x.qty : '') + '</small>' +
          '<a href="' + esc(p.u) + '" target="_blank" rel="noopener">' + esc(p.n) + '</a>' +
          '</span>' +
          '<span class="bld-line-price">' + esc(self.money(p.p * x.qty)) + '</span>' +
          '<span class="bld-line-acts">' +
          '<button type="button" data-bld-goto="' + i + '" aria-label="Change ' + esc(s.label) + '">' + icon('swap', 'ic-14') + '</button>' +
          '<button type="button" data-bld-drop="' + p.id + '" data-step="' + esc(s.key) + '" aria-label="Remove ' + esc(p.n) + '">' + icon('trash', 'ic-14') + '</button>' +
          '</span>' +
          '</li>';
      });
    });

    this.el.lines.innerHTML = lines ||
      '<li class="bld-line-none">Nothing chosen yet. Start with a processor — it sets the socket everything else follows.</li>';

    // Verdict + checklist
    var v = this.verdict();
    this.el.healthTtl.textContent = v.title;
    this.el.healthDot.className = 'bld-health-dot is-' + v.level;
    this.el.issues.innerHTML = v.issues.map(function (i) {
      return '<li class="bld-issue is-' + i.level + '"' + (i.step ? ' data-bld-issue-step="' + esc(i.step) + '"' : '') + '>' +
        icon(i.icon || 'info', 'ic-14') + '<span>' + esc(i.text) + '</span></li>';
    }).join('');

    // Power
    var pw = this.power();
    var pv = this.psuVerdict();
    if (pw.known) {
      this.el.powerVal.textContent = '~' + pw.load + ' W';
      var pct = pv.level !== 'idle'
        ? pv.pct
        : Math.min(100, Math.round((pw.load / (pw.recommend || 1)) * 100));
      this.el.powerBar.style.width = pct + '%';
      this.el.powerBar.className = 'is-' + pv.level;
      this.el.powerNote.textContent = pv.level !== 'idle'
        ? pv.psu.n + ' — ' + pv.w + ' W, running at about ' + pv.pct + '% of capacity.' +
        (pv.vmin && pv.w < pv.vmin ? ' Your graphics card asks for ' + pv.vmin + ' W.' : '')
        : 'A ' + pw.recommend + ' W supply or larger gives comfortable headroom.';
    } else {
      this.el.powerVal.textContent = '—';
      this.el.powerBar.style.width = '0%';
      this.el.powerBar.className = 'is-idle';
      this.el.powerNote.textContent = 'Add a processor and graphics card for a wattage estimate.';
    }

    // Totals
    var t = this.totals();
    this.el.total.textContent = t.price ? this.money(t.price) : '—';
    this.el.mrp.textContent = this.money(t.mrp);
    this.el.save.textContent = this.money(t.save);
    this.el.mrpRow.hidden = t.save <= 0;
    this.el.saveRow.hidden = t.save <= 0;

    // Progress
    var requiredSteps = this.steps.filter(function (s) { return s.required && s.parts.length; });
    var doneRequired = requiredSteps.filter(function (s) { return (self.sel[s.key] || []).length; }).length;
    var pctDone = requiredSteps.length ? doneRequired / requiredSteps.length : 0;
    var C = 2 * Math.PI * 19;
    if (this.el.ring) {
      this.el.ring.style.strokeDasharray = C.toFixed(1);
      this.el.ring.style.strokeDashoffset = (C * (1 - pctDone)).toFixed(1);
    }
    this.el.progCount.textContent = doneRequired + '/' + (requiredSteps.length || 0);
    this.el.progLabel.textContent = 'essentials chosen';

    // Add-to-cart availability
    var n = this.selectedCount();
    var blockers = v.issues.filter(function (i) { return i.level === 'bad'; }).length;
    this.el.add.disabled = n === 0 || this.busy;
    this.el.addLabel.textContent = n === 0
      ? 'Add build to cart'
      : (blockers ? 'Add anyway (' + n + ' part' + (n > 1 ? 's' : '') + ')' : 'Add ' + n + ' part' + (n > 1 ? 's' : '') + ' to cart');
    this.el.add.classList.toggle('is-risky', blockers > 0);

    // Mobile dock
    this.el.dock.hidden = n === 0;
    this.el.dockCount.textContent = n + ' part' + (n === 1 ? '' : 's');
    this.el.dockTotal.textContent = t.price ? this.money(t.price) : '—';
    this.el.dockHealth.className = 'bld-dock-health is-' + v.level;
  };

  /* Edge shadows on the pinned header and totals, shown only when the middle
     region genuinely has more above or below the fold. */
  Builder.prototype.syncScrollEdges = function () {
    var box = this.el.scroll, inner = $('.bld-summary-inner', this.root);
    if (!box || !inner) return;
    var slack = box.scrollHeight - box.clientHeight;
    inner.classList.toggle('has-more-top', slack > 2 && box.scrollTop > 2);
    inner.classList.toggle('has-more-bottom', slack > 2 && box.scrollTop < slack - 2);
  };

  Builder.prototype.renderPresets = function () {
    var presets = (this.cfg.presets || []).filter(function (p) {
      return p && p.ids && p.ids.filter(Boolean).length;
    });
    if (!presets.length) { this.el.presets.hidden = true; return; }

    var self = this;
    // Only offer a machine whose parts this builder can actually load.
    var usable = presets.filter(function (p) {
      return p.ids.filter(function (id) { return id && self.byId[id]; }).length >= 2;
    });
    if (!usable.length) { this.el.presets.hidden = true; return; }

    this.el.presets.hidden = false;
    this.el.presetRail.innerHTML = usable.map(function (p, i) {
      var known = p.ids.filter(function (id) { return id && self.byId[id]; }).length;
      return '<button type="button" class="bld-preset" role="listitem" data-bld-preset="' + i + '">' +
        (p.img ? '<img src="' + esc(p.img) + '" alt="" width="64" height="64" loading="lazy">' : '') +
        '<span class="bld-preset-body">' +
        (p.use ? '<em>' + esc(p.use) + '</em>' : '') +
        '<b>' + esc(p.t) + '</b>' +
        '<small>' + known + ' parts &middot; from ' + esc(self.money(p.p)) + '</small>' +
        '</span>' +
        '<span class="bld-preset-go" aria-hidden="true">' + icon('swap', 'ic-16') + '</span>' +
        '</button>';
    }).join('');
    this._presets = usable;
    this.updatePresetArrows();
  };

  Builder.prototype.updatePresetArrows = function () {
    if (!this.el.presetRail || !this.el.presetPrev || !this.el.presetNext) return;
    var r = this.el.presetRail;
    var maxScroll = r.scrollWidth - r.clientWidth;
    if (maxScroll <= 4) {
      this.el.presetPrev.disabled = true;
      this.el.presetNext.disabled = true;
    } else {
      this.el.presetPrev.disabled = r.scrollLeft <= 4;
      this.el.presetNext.disabled = r.scrollLeft >= maxScroll - 4;
    }
  };

  Builder.prototype.renderNothing = function () {
    var main = $('.bld-main', this.root);
    if (!main) return;
    main.innerHTML = '<div class="bld-empty"><h3 class="d4">The builder has no steps configured</h3>' +
      '<p class="sm">Add at least one <b>Build step</b> block to this section in the theme editor and point it at a collection.</p></div>';
  };

  Builder.prototype.renderAll = function () {
    this.renderSteps();
    this.renderParts();
    this.renderSummary();
    this.syncScrollEdges();
  };

  /* ───────────────────────────────────────────────────── mutations ── */

  Builder.prototype.choose = function (stepKey, id) {
    var step = this.steps.filter(function (s) { return s.key === stepKey; })[0];
    var rec = this.byId[id];
    if (!step || !rec) return;
    if (this.blockReason(rec.part, step)) return;

    var list = this.sel[stepKey] || (this.sel[stepKey] = []);
    var at = list.map(function (x) { return x.id; }).indexOf(id);

    if (step.multi) {
      if (at > -1) list.splice(at, 1);
      else if (list.length < step.max) list.push({ id: id, qty: 1 });
      else { this.flash('This step takes at most ' + step.max + ' parts. Remove one first.'); return; }
    } else {
      if (at > -1) list.length = 0;
      else this.sel[stepKey] = [{ id: id, qty: 1 }];
    }

    this.prune();
    this.persist();
    this.renderAll();
  };

  /* Deliberately choosing a part that conflicts with earlier picks. The new
     part wins and everything it invalidates is cleared, so the cart can still
     never hold a combination that will not physically go together. */
  Builder.prototype.switchTo = function (stepKey, id) {
    var step = this.steps.filter(function (s) { return s.key === stepKey; })[0];
    var rec = this.byId[id];
    if (!step || !rec) return;

    var before = this.describeSelections();
    if (step.multi) {
      var list = this.sel[stepKey] || (this.sel[stepKey] = []);
      if (!this.isSelected(stepKey, id)) {
        if (list.length >= step.max) list.shift();
        list.push({ id: id, qty: 1 });
      }
    } else {
      this.sel[stepKey] = [{ id: id, qty: 1 }];
    }

    this.prune(stepKey);
    var after = this.describeSelections();
    var removed = before.filter(function (n) { return after.indexOf(n) === -1; });
    this.persist();
    this.renderAll();
    this.flash(removed.length
      ? 'Switched to ' + rec.part.n + '. ' + removed.join(' and ') + ' no longer ' +
      (removed.length > 1 ? 'fit and were' : 'fits and was') + ' removed.'
      : 'Switched to ' + rec.part.n + '.');
  };

  Builder.prototype.describeSelections = function () {
    var self = this, out = [];
    this.steps.forEach(function (s) {
      (self.sel[s.key] || []).forEach(function (x) {
        var rec = self.byId[x.id];
        if (rec) out.push(rec.part.n);
      });
    });
    return out;
  };

  Builder.prototype.setQty = function (stepKey, id, delta) {
    var step = this.steps.filter(function (s) { return s.key === stepKey; })[0];
    var list = this.sel[stepKey] || [];
    var hit = list.filter(function (x) { return x.id === id; })[0];
    if (!hit || !step) return;
    hit.qty = Math.max(1, Math.min(step.max, hit.qty + delta));
    this.persist();
    this.renderAll();
  };

  Builder.prototype.drop = function (stepKey, id) {
    var list = this.sel[stepKey] || [];
    this.sel[stepKey] = list.filter(function (x) { return x.id !== id; });
    this.prune();
    this.persist();
    this.renderAll();
  };

  /* A later part can be invalidated by an earlier change (swap the board and
     the DDR5 kit no longer fits). Drop anything that is now impossible and say
     so, rather than quietly carrying an unbuildable configuration to checkout. */
  Builder.prototype.prune = function (protectKey) {
    var self = this, dropped = [];
    var pass = function () {
      var changed = false;
      self.steps.forEach(function (s) {
        if (s.key === protectKey) return;   // never drop what was just chosen
        var keep = [];
        (self.sel[s.key] || []).forEach(function (x) {
          var rec = self.byId[x.id];
          if (!rec) { changed = true; return; }
          var why = self.blockReasonExcluding(rec.part, s, x.id);
          if (why) { dropped.push(rec.part.n); changed = true; }
          else keep.push(x);
        });
        self.sel[s.key] = keep;
      });
      return changed;
    };
    // Two passes settle the common cascades without risking a loop.
    if (pass()) pass();
    if (dropped.length) {
      this.flash(uniq(dropped).join(' and ') + ' no longer fits, so ' +
        (dropped.length > 1 ? 'they were' : 'it was') + ' removed.');
    }
  };

  /* blockReason for an already-chosen part must ignore that part's own step,
     otherwise a selection would always be found to conflict with itself. */
  Builder.prototype.blockReasonExcluding = function (part, step, selfId) {
    var saved = this.sel[step.key];
    this.sel[step.key] = (saved || []).filter(function (x) { return x.id !== selfId; });
    var why;
    try { why = this.blockReason(part, step); }
    finally { this.sel[step.key] = saved; }
    return why;
  };

  Builder.prototype.goto = function (i) {
    if (i < 0 || i >= this.steps.length) return;
    this.active = i;
    this.query = '';
    this.facet = null;
    this.facetsOpen = false;
    if (this.el.search) this.el.search.value = '';
    if (this.el.searchClr) this.el.searchClr.hidden = true;
    this.renderAll();
    var main = $('.bld-main', this.root);
    if (main && window.innerWidth < 1024) {
      var top = main.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }
  };

  Builder.prototype.reset = function () {
    var self = this;
    this.steps.forEach(function (s) { self.sel[s.key] = []; });
    this.active = 0;
    this.query = '';
    this.facet = null;
    if (this.el.search) this.el.search.value = '';
    try { localStorage.removeItem(LS_KEY); } catch (e) { }
    this.writeUrl(true);
    this.renderAll();
    this.flash('Build cleared.');
  };

  Builder.prototype.flash = function (msg) {
    if (!this.el.status) return;
    this.el.status.textContent = msg || '';
    this.el.status.classList.toggle('is-on', !!msg);
    clearTimeout(this._flashT);
    if (msg) this._flashT = setTimeout(function () {
      this.el.status.textContent = '';
      this.el.status.classList.remove('is-on');
    }.bind(this), 6000);
  };

  /* ─────────────────────────────────────────── persistence & sharing ── */

  Builder.prototype.encode = function () {
    var self = this, parts = [];
    this.steps.forEach(function (s) {
      (self.sel[s.key] || []).forEach(function (x) {
        parts.push(s.key + '.' + x.id + (x.qty > 1 ? '.' + x.qty : ''));
      });
    });
    return parts.join(',');
  };

  Builder.prototype.decode = function (str) {
    var self = this, applied = 0, lost = 0;
    this.steps.forEach(function (s) { self.sel[s.key] = []; });
    String(str || '').split(',').forEach(function (chunk) {
      var bits = chunk.split('.');
      if (bits.length < 2) return;
      var key = bits[0], id = parseInt(bits[1], 10), qty = parseInt(bits[2] || '1', 10) || 1;
      var step = self.steps.filter(function (s) { return s.key === key; })[0];
      var rec = self.byId[id];
      if (!step || !rec) { lost++; return; }
      var list = self.sel[key];
      if (!step.multi && list.length) return;
      if (list.length >= step.max) return;
      list.push({ id: id, qty: Math.min(qty, step.max) });
      applied++;
    });
    return { applied: applied, lost: lost };
  };

  Builder.prototype.persist = function () {
    var payload = this.encode();
    try {
      if (payload) localStorage.setItem(LS_KEY, JSON.stringify({ v: 1, at: Date.now(), b: payload }));
      else localStorage.removeItem(LS_KEY);
    } catch (e) { /* private mode — the URL still carries the build */ }
    this.writeUrl();
  };

  Builder.prototype.writeUrl = function (clear) {
    if (!window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    var payload = clear ? '' : this.encode();
    url.searchParams.delete('parts');
    if (payload) url.searchParams.set('build', payload);
    else url.searchParams.delete('build');
    window.history.replaceState({}, '', url.toString());
  };

  /* `?parts=id,id,id` — a plain product-id list, used by the Pre-Built PC page's
     "Customise" link. Step keys are resolved here rather than encoded in the URL,
     so re-ordering the builder's blocks can never break an existing link. */
  Builder.prototype.decodeParts = function (csv) {
    var self = this, applied = 0, lost = 0;
    this.steps.forEach(function (s) { self.sel[s.key] = []; });
    String(csv || '').split(',').forEach(function (raw) {
      var id = parseInt(String(raw).replace(/\D/g, ''), 10);
      if (!id) return;
      var rec = self.byId[id];
      if (!rec) { lost++; return; }
      var s = rec.step, list = self.sel[s.key];
      if (!s.multi && list.length) return;
      if (list.length >= s.max) return;
      list.push({ id: id, qty: 1 });
      applied++;
    });
    return { applied: applied, lost: lost };
  };

  Builder.prototype.restore = function () {
    var params = new URL(window.location.href).searchParams;

    var fromParts = params.get('parts');
    if (fromParts) {
      var pr = this.decodeParts(fromParts);
      if (pr.applied) {
        this.prune();
        this.persist();
        this.jumpToFirstOpen();
        this.flash('Loaded ' + pr.applied + ' parts from that build — change anything you like.' +
          (pr.lost ? ' ' + pr.lost + ' part' + (pr.lost > 1 ? 's are' : ' is') + ' not offered in this builder.' : ''));
        return;
      }
    }

    var fromUrl = params.get('build');
    if (fromUrl) {
      var r = this.decode(fromUrl);
      if (r.applied) {
        this.jumpToFirstOpen();
        if (r.lost) this.flash(r.lost + ' part' + (r.lost > 1 ? 's are' : ' is') + ' no longer stocked and could not be loaded.');
        return;
      }
    }
    var saved = null;
    try { saved = JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch (e) { }
    if (saved && saved.b) {
      var res = this.decode(saved.b);
      if (res.applied) {
        this.jumpToFirstOpen();
        this.flash('Picked up where you left off. ' +
          (res.lost ? res.lost + ' part' + (res.lost > 1 ? 's were' : ' was') + ' dropped — no longer stocked.' : ''));
      }
    }
  };

  Builder.prototype.jumpToFirstOpen = function () {
    for (var i = 0; i < this.steps.length; i++) {
      var s = this.steps[i];
      if (s.required && s.parts.length && !(this.sel[s.key] || []).length) { this.active = i; return; }
    }
    this.active = 0;
  };

  Builder.prototype.loadPreset = function (i) {
    var preset = (this._presets || [])[i];
    if (!preset) return;
    var self = this, applied = 0, skipped = 0;

    this.steps.forEach(function (s) { self.sel[s.key] = []; });
    preset.ids.filter(Boolean).forEach(function (id) {
      var rec = self.byId[id];
      if (!rec) { skipped++; return; }
      var s = rec.step, list = self.sel[s.key];
      if (!s.multi && list.length) return;
      if (list.length >= s.max) return;
      list.push({ id: id, qty: 1 });
      applied++;
    });

    this.prune();
    this.persist();
    this.jumpToFirstOpen();
    this.renderAll();
    this.flash(preset.t + ' loaded — ' + applied + ' parts. Change anything you like.' +
      (skipped ? ' ' + skipped + ' part' + (skipped > 1 ? 's are' : ' is') + ' not offered in this builder.' : ''));

    var main = $('.bld-shell', this.root);
    if (main) window.scrollTo({ top: main.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
  };

  Builder.prototype.share = function () {
    this.writeUrl();
    var url = window.location.href;
    var done = function (msg) { this.flash(msg); }.bind(this);

    if (navigator.share && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
      navigator.share({ title: document.title, url: url }).then(function () { }, function () { });
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(
        function () { done('Build link copied to your clipboard.'); },
        function () { done('Copy this page\'s address to share your build.'); }
      );
      return;
    }
    done('Copy this page\'s address to share your build.');
  };

  Builder.prototype.printSheet = function () {
    var self = this, t = this.totals(), pw = this.power(), v = this.verdict();
    var rows = '', rowIndex = 0, totalItems = 0;
    this.writeUrl();
    var permalink = window.location.href;
    var refCode = 'SC-' + Math.random().toString(36).slice(2, 7).toUpperCase();
    var now = new Date();
    var dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
      now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    this.steps.forEach(function (s) {
      (self.sel[s.key] || []).forEach(function (x) {
        var rec = self.byId[x.id];
        if (!rec) return;
        rowIndex++;
        totalItems += x.qty;
        var part = rec.part;
        rows += '<tr>' +
          '<td class="bld-print-idx">' + rowIndex + '</td>' +
          '<td class="bld-print-role"><span class="bld-print-role-badge">' + esc(s.label) + '</span></td>' +
          '<td class="bld-print-part"><strong class="bld-print-title">' + esc(part.t) + '</strong></td>' +
          '<td class="bld-print-qty">' + x.qty + '</td>' +
          '<td class="bld-print-price r">' + esc(self.money(part.p)) + '</td>' +
          '<td class="bld-print-total r"><strong>' + esc(self.money(part.p * x.qty)) + '</strong></td>' +
          '</tr>';
      });
    });

    if (!rows) { this.flash('Choose some parts first.'); return; }

    var node = document.getElementById('sc-bld-print-sheet') || document.createElement('div');
    node.id = 'sc-bld-print-sheet';
    node.className = 'bld-print';
    node.innerHTML =
      '<div class="bld-print-page">' +
      '<div class="bld-print-header">' +
      '<div class="bld-print-brand-wrap">' +
      '<div class="bld-print-logo">STARTUS<span>CLOUD</span></div>' +
      '<div class="bld-print-tagline">Custom PC & Hardware</div>' +
      '</div>' +
      '<div class="bld-print-meta-wrap">' +
      '<div class="bld-print-doc-title">BUILD SPECIFICATION & QUOTATION</div>' +
      '<div class="bld-print-doc-meta">' +
      '<span><strong>Ref:</strong> ' + esc(refCode) + '</span>' +
      '<span><strong>Date:</strong> ' + esc(dateStr) + '</span>' +
      (pw.known ? '<span><strong>Est. Power:</strong> ~' + pw.load + 'W</span>' : '') +
      '</div>' +
      '</div>' +
      '</div>' +

      '<table class="bld-print-table">' +
      '<thead>' +
      '<tr>' +
      '<th style="width: 24px;">#</th>' +
      '<th style="width: 110px;">Category</th>' +
      '<th>Component</th>' +
      '<th class="c" style="width: 44px;">Qty</th>' +
      '<th class="r" style="width: 90px;">Unit Price</th>' +
      '<th class="r" style="width: 100px;">Total</th>' +
      '</tr>' +
      '</thead>' +
      '<tbody>' + rows + '</tbody>' +
      '<tfoot>' +
      (t.save > 0 ?
        '<tr>' +
        '<td colspan="4" class="bld-print-tf-empty"></td>' +
        '<th class="r">Total List Price</th>' +
        '<td class="r"><s>' + esc(this.money(t.mrp)) + '</s></td>' +
        '</tr>' +
        '<tr class="bld-print-savings-row">' +
        '<td colspan="4" class="bld-print-tf-empty"></td>' +
        '<th class="r">Instant Bundle Savings</th>' +
        '<td class="r" style="color:#b91c1c;font-weight:700;">- ' + esc(this.money(t.save)) + '</td>' +
        '</tr>' : '') +
      '<tr class="bld-print-final-row">' +
      '<td colspan="4" class="bld-print-tf-note">' +
      '<strong>Startus Cloud Assurance:</strong> 100% genuine components with official brand warranties. Fully assembled, cable-managed, and bench stress-tested.' +
      '</td>' +
      '<th class="r">Final Build Total</th>' +
      '<td class="r bld-print-grand-price">' + esc(this.money(t.price)) + '</td>' +
      '</tr>' +
      '</tfoot>' +
      '</table>' +

      '<div class="bld-print-footer">' +
      '<div class="bld-print-footer-info">' +
      '<div><strong>Startus Cloud Hardware & Performance</strong> &middot; support@startuscloud.com &middot; startuscloud.com</div>' +
      '</div>' +
      '</div>' +
      '</div>';

    if (!node.parentNode) document.body.appendChild(node);
    window.print();
  };

  /* ──────────────────────────────────────────────────────────── cart ── */

  Builder.prototype.addToCart = function () {
    if (this.busy) return;
    var self = this;
    var items = [];
    var ref = 'PC-' + Math.random().toString(36).slice(2, 6).toUpperCase();
    this.writeUrl();
    var permalink = window.location.href;

    this.steps.forEach(function (s) {
      (self.sel[s.key] || []).forEach(function (x) {
        var rec = self.byId[x.id];
        if (!rec || !rec.part.vid) return;
        items.push({
          id: rec.part.vid,
          quantity: x.qty,
          properties: {
            'Custom build': ref,
            'Role': s.label,
            '_build_link': permalink
          }
        });
      });
    });

    if (!items.length) { this.flash('Choose some parts first.'); return; }

    this.busy = true;
    this.el.add.disabled = true;
    this.el.add.classList.add('is-loading');
    this.flash('Adding ' + items.length + ' parts to your cart…');

    var url = (window.Shopify && Shopify.routes && Shopify.routes.cart_add_url)
      ? Shopify.routes.cart_add_url + '.js' : '/cart/add.js';

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: ['cart-count-badge', 'offcanvas-cart'],
        sections_url: window.location.pathname
      })
    })
      .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
      .then(function (r) {
        if (!r.ok) throw r.data;
        self.refreshCartUi(r.data.sections);
        self.openCart();
        self.flash('Build ' + ref + ' added — ' + items.length + ' parts in your cart.');
      })
      .catch(function (err) {
        var msg = (err && (err.description || err.message)) || 'Something went wrong adding the build.';
        // 422 rejects the whole request, so name the part and let the shopper act.
        self.flash(msg + ' Remove or swap that part and try again.');
        console.error('[sc-pc-builder] cart add failed', err);
      })
      .finally(function () {
        self.busy = false;
        self.el.add.classList.remove('is-loading');
        self.renderSummary();
      });
  };

  Builder.prototype.refreshCartUi = function (sections) {
    var ktCart = document.querySelector('kt-cart');
    if (ktCart && typeof ktCart.reloadCartElements === 'function') { ktCart.reloadCartElements(sections); return; }
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var doc = new DOMParser().parseFromString(sections[id], 'text/html');
      if (id === 'cart-count-badge') {
        var fresh = doc.querySelector('.cart-count-badge');
        if (fresh) $$('.cart-count-badge').forEach(function (b) { b.replaceWith(fresh.cloneNode(true)); });
      } else if (id === 'offcanvas-cart') {
        var box = doc.querySelector('.cart-container');
        if (box) $$('.cart-container').forEach(function (c) { c.replaceWith(box.cloneNode(true)); });
      }
    });
  };

  Builder.prototype.openCart = function () {
    var oc = document.getElementById('offcanvas-cart');
    if (oc && window.bootstrap && window.bootstrap.Offcanvas) {
      window.bootstrap.Offcanvas.getOrCreateInstance(oc).show();
    }
  };

  /* ───────────────────────────────────────────────────────── events ── */

  Builder.prototype.sheet = function (open) {
    this.root.classList.toggle('bld-sheet-open', open);
    var self = this;
    setTimeout(function () { self.syncScrollEdges(); }, 320);
    this.el.scrim.hidden = !open;
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) {
      var close = $('[data-bld-sheet-close]', this.root);
      if (close) close.focus();
    }
  };

  Builder.prototype.bind = function () {
    var self = this, r = this.root;

    r.addEventListener('click', function (e) {
      var t = e.target;

      var goto = t.closest('[data-bld-goto]');
      if (goto) { self.goto(parseInt(goto.getAttribute('data-bld-goto'), 10)); self.sheet(false); return; }

      var card = t.closest('[data-bld-part]');
      if (card) {
        var id = parseInt(card.getAttribute('data-bld-part'), 10);
        var key = card.getAttribute('data-step');
        if (t.closest('[data-bld-qty]')) {
          self.setQty(key, id, parseInt(t.closest('[data-bld-qty]').getAttribute('data-bld-qty'), 10));
          return;
        }
        if (t.closest('[data-bld-remove]')) { self.drop(key, id); return; }
        if (t.closest('[data-bld-switch]')) { self.switchTo(key, id); return; }
        if (t.closest('[data-bld-choose]') || (!t.closest('a') && !t.closest('button'))) {
          self.choose(key, id);
          return;
        }
      }

      var drop = t.closest('[data-bld-drop]');
      if (drop) { self.drop(drop.getAttribute('data-step'), parseInt(drop.getAttribute('data-bld-drop'), 10)); return; }

      var facet = t.closest('[data-bld-facet]');
      if (facet) {
        var fid = facet.getAttribute('data-bld-facet'), fval = facet.getAttribute('data-value');
        self.facet = (self.facet && self.facet.id === fid && self.facet.value === fval) ? null : { id: fid, value: fval };
        self.renderParts();
        return;
      }

      var preset = t.closest('[data-bld-preset]');
      if (preset) { self.loadPreset(parseInt(preset.getAttribute('data-bld-preset'), 10)); return; }

      var issue = t.closest('[data-bld-issue-step]');
      if (issue) {
        var want = issue.getAttribute('data-bld-issue-step');
        var idx = self.steps.map(function (s) { return s.key; }).indexOf(want);
        if (idx > -1) { self.goto(idx); self.sheet(false); }
        return;
      }

      if (t.closest('[data-bld-preset-prev]')) {
        if (self.el.presetRail) {
          self.el.presetRail.scrollBy({ left: -280, behavior: 'smooth' });
        }
        return;
      }
      if (t.closest('[data-bld-preset-next]')) {
        if (self.el.presetRail) {
          self.el.presetRail.scrollBy({ left: 280, behavior: 'smooth' });
        }
        return;
      }
      if (t.closest('[data-bld-prev]')) { self.goto(self.active - 1); return; }
      if (t.closest('[data-bld-next]')) { self.goto(self.active + 1); return; }
      if (t.closest('[data-bld-skip]') || t.closest('[data-bld-skip-2]')) { self.goto(self.active + 1); return; }
      if (t.closest('[data-bld-reset]')) { self.reset(); return; }
      if (t.closest('[data-bld-add]')) { self.addToCart(); return; }
      if (t.closest('[data-bld-share]')) { self.share(); return; }
      if (t.closest('[data-bld-print]')) { self.printSheet(); return; }
      if (t.closest('[data-bld-sheet-open]')) { self.sheet(true); return; }
      if (t.closest('[data-bld-sheet-close]') || t.closest('[data-bld-scrim]')) { self.sheet(false); return; }
      if (t.closest('[data-bld-show-all]')) {
        self.compatOnly = false; self.el.compat.checked = false; self.renderParts(); return;
      }
      if (t.closest('[data-bld-facet-more]')) { self.facetsOpen = !self.facetsOpen; self.renderParts(); return; }
      if (t.closest('[data-bld-clear-filters]')) {
        self.query = ''; self.facet = null; self.el.search.value = '';
        self.el.searchClr.hidden = true; self.renderParts(); return;
      }
    });

    if (this.el.search) {
      var debounce;
      this.el.search.addEventListener('input', function () {
        clearTimeout(debounce);
        var v = this.value;
        debounce = setTimeout(function () {
          self.query = v;
          self.el.searchClr.hidden = !v;
          self.renderParts();
        }, 140);
      });
      this.el.search.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { this.value = ''; self.query = ''; self.el.searchClr.hidden = true; self.renderParts(); }
      });
    }
    if (this.el.searchClr) this.el.searchClr.addEventListener('click', function () {
      self.el.search.value = ''; self.query = ''; this.hidden = true; self.renderParts(); self.el.search.focus();
    });
    if (this.el.sort) this.el.sort.addEventListener('change', function () { self.sort = this.value; self.renderParts(); });
    if (this.el.compat) this.el.compat.addEventListener('change', function () { self.compatOnly = this.checked; self.renderParts(); });

    if (this.el.presetRail) {
      this.el.presetRail.addEventListener('scroll', function () { self.updatePresetArrows(); }, { passive: true });
    }
    if (window.ResizeObserver && this.el.presetRail) {
      new ResizeObserver(function () { self.updatePresetArrows(); }).observe(this.el.presetRail);
    }

    if (this.el.scroll) {
      this.el.scroll.addEventListener('scroll', function () { self.syncScrollEdges(); }, { passive: true });
    }
    if (window.ResizeObserver && this.el.scroll) {
      new ResizeObserver(function () { self.syncScrollEdges(); }).observe(this.el.scroll);
    } else {
      window.addEventListener('resize', function () { self.syncScrollEdges(); });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.root.classList.contains('bld-sheet-open')) self.sheet(false);
    });

    window.addEventListener('popstate', function () {
      var b = new URL(window.location.href).searchParams.get('build');
      if (b == null) return;
      self.decode(b);
      self.renderAll();
    });
  };

  /* ─────────────────────────────────────────────────────────── boot ── */

  function boot(scope) {
    $$('[data-sc-builder]', scope || document).forEach(function (el) {
      if (el.__scBuilder) return;
      el.__scBuilder = new Builder(el);
    });
  }

  SC.PCBuilder = Builder;
  SC.initPCBuilder = boot;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); });
  else boot(document);

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-sc-builder]')) {
      var host = e.target.querySelector('[data-sc-builder]');
      if (host) host.__scBuilder = null;
      boot(e.target);
    }
  });
})();
